import { createContext, useContext, type FC, type ReactNode } from 'react';

const PortalContainerContext = createContext<HTMLElement | null>(null);

/**
 * The portal target for every `TooltipContent`, `DialogContent`, and
 * `DropdownMenuContent` below this provider. A `container` prop on one of those
 * components wins over the provider.
 *
 * Set `container` to the fullscreen element while a surface is fullscreen. The
 * browser paints only the fullscreen element's own subtree, so an overlay left on
 * `document.body` never appears. Pass `null` to portal to `document.body`.
 */
export const PortalContainerProvider: FC<{
  container: HTMLElement | null;
  children: ReactNode;
}> = ({ container, children }) => (
  <PortalContainerContext.Provider value={container}>{children}</PortalContainerContext.Provider>
);

/** The portal target from the nearest `PortalContainerProvider`, or `null`. */
export const usePortalContainer = (): HTMLElement | null => useContext(PortalContainerContext);
